import { Button, Drawer } from "@components";
import { useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import { RFVProfilesChart } from "./RFVProfilesChart";
import { BubbleChart } from "../../BubbleChart";
import { Numbers } from "shared/utils/Numbers";

const Trend = ({ growth, invertColorSign }) => {
  const upColor = invertColorSign ? "#e48186" : "#51D9A3";
  const downColor = invertColorSign ? "#51D9A3" : "#e48186";

  const isNegative = growth < 0;
  const sign = isNegative ? "" : growth > 0 ? "+" : "";
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? downColor : upColor;

  return (
    <p className={styles.growth} style={{ color: iconColor }}>
      {sign}
      {Numbers.number(growth, 2)}%{" "}
      <i className={iconClass} style={{ color: iconColor }} />
    </p>
  );
};

const calculatePercentage = (part, total) => {
  if (!total || isNaN(part) || isNaN(total)) return 0;
  return (part / total) * 100;
};

const groups = (data) => {
  const profiles = data?.profiles || [];
  const total_customers = data?.statistics?.total_customers?.value || 0;

  return [
    {
      alias: "CAMPEOES",
      tooltip:
        "Compraram recentemente, e tem muita frequência e/ou gastam muito",
      name: "Campeões",
      borderColor: "#4bca97",
      valueColor: "rgba(81, 217, 163, 0.15)",
      bgColor: "#212825",
      total: profiles?.campeoes?.value || 0,
      growth: profiles?.campeoes?.growth || 0,
      percentage: calculatePercentage(
        profiles?.campeoes?.value,
        total_customers
      ),
    },
    {
      alias: "PROMISSORES",
      name: "Promissores",
      tooltip:
        "Compraram recentemente, e tem baixa ou média frequência e/ou baixo ou médio valor",
      borderColor: "#de52b4",
      valueColor: "rgba(222, 82, 180, 0.15)",
      bgColor: "#282126",
      total: profiles?.promissores?.value || 0,
      growth: profiles?.promissores?.growth || 0,
      percentage: calculatePercentage(
        profiles?.promissores?.value,
        total_customers
      ),
    },
    {
      alias: "REQUER_ATENCAO",
      name: "Requer atenção",
      tooltip:
        "Compraram há algum tempo, mas tem muita frequência e/ou gastam muito.",
      borderColor: "#d9d26c",
      valueColor: "rgba(217, 210, 108, 0.15)",
      bgColor: "#282722",
      total: profiles?.requer_atencao?.value || 0,
      growth: profiles?.requer_atencao?.growth || 0,
      percentage: calculatePercentage(
        profiles?.requer_atencao?.value,
        total_customers
      ),
    },
    {
      alias: "EM_RISCO",
      name: "Em risco",
      tooltip:
        "Compraram há algum tempo, e tem frequência média com valor médio ou ruim",
      borderColor: "#c74f53",
      valueColor: "rgba(219, 87, 91, 0.15)",
      bgColor: "#282122",
      total: profiles?.em_risco?.value || 0,
      growth: profiles?.em_risco?.growth || 0,
      percentage: calculatePercentage(
        profiles?.em_risco?.value,
        total_customers
      ),
    },
    {
      alias: "DESLIGANDO",
      name: "Desligando",
      tooltip:
        "Compraram há algum tempo, e tem frequência média e/ou ruim, com valor médio e/ou ruim",
      borderColor: "#515d73",
      valueColor: "rgba(81, 94, 115, 0.15)",
      bgColor: "#212223",
      total: profiles?.desligando?.value || 0,
      growth: profiles?.desligando?.growth || 0,
      percentage: calculatePercentage(
        profiles?.desligando?.value,
        total_customers
      ),
    },
    {
      alias: "HIBERNANDO",
      name: "Hibernando",
      tooltip:
        "Compraram há muito tempo, porém possuem boa ou média frequência, com bom ou médio valor",
      borderColor: "#a67666",
      valueColor: "rgba(166, 118, 102, 0.15)",
      bgColor: "#252322",
      total: profiles?.hibernando?.value || 0,
      growth: profiles?.hibernando?.growth || 0,
      percentage: calculatePercentage(
        profiles?.hibernando?.value,
        total_customers
      ),
    },
    {
      alias: "ABANDONADOS",
      name: "Abandonados",
      tooltip:
        "Compraram há muito tempo, e tem frequência média e/ou ruim, com valor médio e/ou ruim",
      borderColor: "#fff",
      valueColor: "rgba(255, 255, 255, 0.15)",
      bgColor: "#2a2a2a",
      total: profiles?.abandonados?.value || 0,
      growth: profiles?.abandonados?.growth || 0,
      percentage: calculatePercentage(
        profiles?.abandonados?.value,
        total_customers
      ),
    },
  ];
};

export default function RFVProfiles({ data, context }) {
  const [selectedData, setSelectedData] = useState(null);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center relative gap-5">
          <p className={styles.label}>Perfis RFV</p>
          <span
            className={styles.info}
            title="Quantidade de clientes por perfil RFV."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <Button
          design="default-icon-mini-3"
          onClick={() =>
            setSelectedData({
              name: "Perfis RFV",
            })
          }
        >
          <i className="fas fa-chart-waterfall" />
        </Button>
      </div>

      <div className={styles.container}>
        <div className={styles.customers}>
          <div className={styles.grid}>
            {groups(data).map((group, index) => {
              const total = groups(data).reduce(
                (acc, item) => acc + Number(item.total),
                0
              );

              const percent = groups(data).map((item) => {
                const percentual = (Number(item.total) / total) * 100;
                return percentual.toFixed(2);
              });

              const percentNumber = parseFloat(
                Numbers.number(percent[index]).replace(",", ".")
              );

              return (
                <div className={styles.category} key={group.alias}>
                  <div
                    className={styles.border}
                    style={{
                      backgroundColor: group.borderColor || "#fff",
                    }}
                  />
                  <div className="d-flex align-center">
                    <span
                      className={styles.medal}
                      style={{
                        backgroundColor: group.valueColor || "#fff",
                        color: group.borderColor || "#000",
                      }}
                    >
                      <i className="fal fa-medal" />
                    </span>
                    <p
                      className={styles.percent}
                      style={{ backgroundColor: group.valueColor || "#fff" }}
                    >
                      {Numbers.number(percent[index])}%
                    </p>
                    <p className={styles.name}>{group.name}</p>
                    <span className={styles.info} title={group.tooltip}>
                      <i className="fal fa-info-circle" />
                    </span>
                  </div>
                  <div
                    className={styles.percentage}
                    style={{
                      width: `${percentNumber}%`,
                      backgroundColor: group.bgColor || "#fff",
                    }}
                  />
                  <div className="d-flex align-center">
                    <p className={styles.value}>
                      {Number(group.total).toLocaleString("pt-BR")} clientes
                    </p>
                    <Trend
                      growth={group.growth}
                      invertColorSign={[
                        "REQUER_ATENCAO",
                        "EM_RISCO",
                        "DESLIGANDO",
                        "HIBERNANDO",
                        "ABANDONADOS",
                      ].includes(group.alias)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.bubbleChart}>
          <span className={styles.circleA} />
          <span className={styles.circleB} />
          <span className={styles.circleC} />
          <div
            style={{
              width: "100%",
              height: 320,
            }}
          >
            <BubbleChart
              data={{
                name: "root",
                children: groups(data).map((group) => ({
                  name: group.name,
                  value: group.total,
                  color: group.borderColor,
                })),
              }}
              height={320}
            />
          </div>
        </div>
      </div>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <RFVProfilesChart
          selectedData={selectedData}
          onClose={() => setSelectedData(false)}
          context={context}
          startOn2025
        />
      </Drawer>
    </div>
  );
}
